import React, { useEffect } from 'react';
import Select from "@/components/molecules/Select";
import PortalSelect from '@/components/molecules/PortalSelect';
import { useState } from "react";

interface Option {
  id: string | number;
  label: string;
  countries: { id: string | number; name: string, status: boolean; children: { id: string | number; name: string }[] }[];
  statuses: { id: string | number; name: string }[];
  types: { id: string | number; name: string }[];
  departments: { id: string | number; name: string }[];
  functions: { id: string | number; name: string }[];
  internalTypes: { id: string | number; name: string }[];
  internalOwners: { id: string | number; name: string; email: string }[];
}

interface FilterOption {
  id: string;
  label: string;
  options: {
    value: string | number;
    label: string;
    countries?: { id: string | number; name: string }[],
    statuses?: { id: string | number; name: string }[],
    types?: { id: string | number; name: string }[],
    departments?: { id: string | number; name: string }[],
    functions?: { id: string | number; name: string }[],
    internalTypes?: { id: string | number; name: string }[],
    internalOwners?: { id: string | number; name: string; email: string }[],
  }[];
  selected: (string | number)[];
}

interface TicketFilterProps {
  filters: FilterOption[];
  initialFilters: { [key: string]: (string | number)[] };
  onFilterChange: (filterId: string, value: (string | number)[] | null) => void;
  currentUser: any;
}

const TicketFilter: React.FC<TicketFilterProps> = ({ filters, initialFilters, onFilterChange, currentUser }) => {
  const [isVisibleOtherFilters, setIsVisibleOtherFilters] = useState<boolean>(currentUser?.roles?.some((role: any) => role.name === "client"));
  const [relatedOptions, setRelatedOptions] = useState<any[]>([]);
  
  useEffect(() => {
    if (currentUser?.roles?.some((role: any) => role.name === "client")) {

      setRelatedOptions(filters[0]?.options || []);
    }
  }, [currentUser, initialFilters]);

  const handleChange = (filterId: string, value: (string | number)[], initialValue: (string | number)[]) => {
    onFilterChange(filterId, value.length ? value : null);
  
    if (value.length && filterId === 'account_ids') {
      setIsVisibleOtherFilters(true)
      const accountFilter = filters.find(filter => filter.id === 'account_ids');
      if (accountFilter) {
        const filteredOptions = accountFilter.options
          .filter(option => value.includes(option.value.toString()))
          .map(option => ({
            id: option.value,
            label: option.label,
            countries: option.countries,
            statuses: option.statuses,
            types: option.types,
            departments: option.departments,
            functions: option.functions,
            internalTypes: option.internalTypes,
            internalOwners: option.internalOwners,
          }));
        
        
        setRelatedOptions(filteredOptions);
      }
    } 

    if (value.length == 0 && filterId === 'account_ids' && initialValue.length == 0){
      setIsVisibleOtherFilters(false);
      setRelatedOptions([]);
      onFilterChange('country_ids', null);
      onFilterChange('ticket_status_ids', null);
      onFilterChange('internal_owner_ids', null);
      onFilterChange('ticket_type_ids', null);
      onFilterChange('internal_ticket_type_ids', null);
      onFilterChange('function_ids', null);
      onFilterChange('department_ids', null);
    }
  };

  const getSelectOptions = (options: Option[], field: keyof Option) => {
    if (field === 'statuses' || field === 'internalTypes' || field === 'internalOwners') {
      const uniqueItemsMap = new Map();
 
      options.flatMap(option => option[field] || []).forEach(item => {
        if (!uniqueItemsMap.has(item.id)) {
          uniqueItemsMap.set(item.id, item.name);
        }
      });
  
      const uniqueItems = Array.from(uniqueItemsMap, ([id, name]) => ({
        label: name,
        value: id.toString(),
        disabled: false,
      }));
  
      return uniqueItems;
    }

    return options.flatMap(option => {
      const items = option[field] as { id: string | number; name: string; children?: { id: string | number; name: string }[] }[];
      // Check if any country in the `countries` array has non-empty `children`
      const hasChildren = items.some(item => item.children && item.children.length > 0);
  
      if (!hasChildren) {
        // Case when `children` is empty: disable the option and include its `countries`
        return [
          {
            label: `${option.label} ${field.charAt(0).toUpperCase() + field.slice(1)}`,
            value: `account-${option.id}-${field}`,
            disabled: true,
          },
          ...items.map(item => ({
            label: item.name,
            value: item.id.toString(),
            disabled: false,
          }))
        ];
      }
  
      // Case when `children` is not empty: disable the option and its `countries`,
      // but include `children` as selectable options without disabling
      return [
        {
          label: `${option.label} ${field.charAt(0).toUpperCase() + field.slice(1)}`,
          value: `account-${option.id}-${field}`,
          disabled: true,
        },
        ...items.flatMap(item => [
          {
            label: `Region: ${item.name}`,
            value: item.id.toString(),
            disabled: true,
          },
          ...((item.children || []).map(child => ({
            label: child.name,
            value: child.id.toString(),
            disabled: false,
          })))
        ])
      ];
    });
  };

  return (
    <div className="mb-4 flex flex-wrap gap-4">
      {!currentUser?.roles?.some((role: any) => role.name === "client") && (
        filters.map(filter => (
          <div key={filter.id} className="flex flex-col w-60" style={{ flexBasis: isVisibleOtherFilters ? "25%" : "25%" }}>
            <label htmlFor={filter.id} className="mr-2 text-sm">{filter.label}:</label>
            <PortalSelect
              multiple
              options={
                filter.options.map((option) => ({
                  label: option.label,
                  value: option.value.toString(),
                })) || []
              }
              value={filter.selected}
              onChange={(value) => handleChange(filter.id, value, filter.selected)}
              className='w-100'
              enabledSearch={true}
            />
          </div>
        ))
      )}
      {isVisibleOtherFilters && (
        <>
          <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
            <label htmlFor="statuses" className="mr-2 text-sm">By Status:</label>
            <PortalSelect
              multiple
              options={getSelectOptions(relatedOptions, "statuses")}
              value={initialFilters['ticket_status_ids']}
              onChange={(value) => onFilterChange('ticket_status_ids', value)}
              className= 'w-100'
              enabledSearch={true}
            />
          </div>
          {!currentUser?.roles?.some((role: any) => role.name === "client") && (
            <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
              <label htmlFor="statuses" className="mr-2 text-sm">By Internal Owner:</label>
              <PortalSelect
                multiple
                options={getSelectOptions(relatedOptions, "internalOwners")}
                value={initialFilters['internal_owner_ids']}
                onChange={(value) => onFilterChange('internal_owner_ids', value)}
                className= 'w-100'
                enabledSearch={true}
              />
            </div>
          )}
          <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
            <label htmlFor="statuses" className="mr-2 text-sm">By Type:</label>
            <PortalSelect
              multiple
              options={getSelectOptions(relatedOptions, "types")}
              value={initialFilters['ticket_type_ids']}
              onChange={(value) => onFilterChange('ticket_type_ids', value)}
              className= 'w-100'
              enabledSearch={true}
            />
          </div>
          <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
            <label htmlFor="statuses" className="mr-2 text-sm">By Function:</label>
            <PortalSelect
              multiple
              options={getSelectOptions(relatedOptions, "functions")}
              value={initialFilters['function_ids']}
              onChange={(value) => onFilterChange('function_ids', value)}
              className= 'w-100'
              enabledSearch={true}
            />
          </div>
          <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
            <label htmlFor="statuses" className="mr-2 text-sm">By Department:</label>
            <PortalSelect
              multiple
              options={getSelectOptions(relatedOptions, "departments")}
              value={initialFilters['department_ids']}
              onChange={(value) => onFilterChange('department_ids', value)}
              className= 'w-100'
              enabledSearch={true}
            />
          </div>
          <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
            <label htmlFor="countries" className="mr-2 text-sm">By Country:</label>
            <PortalSelect
              multiple
              options={getSelectOptions(relatedOptions, "countries")}
              value={initialFilters['country_ids']}
              onChange={(value) => onFilterChange('country_ids', value)}
              className= 'w-100'
              enabledSearch={true}
            />
          </div>
          {!currentUser?.roles?.some((role: any) => role.name === "client") && (
            <div className="flex flex-col w-60" style={{ flexBasis: "25%" }}>
              <label htmlFor="statuses" className="mr-2 text-sm">By Internal Type:</label>
              <PortalSelect
                multiple
                options={getSelectOptions(relatedOptions, "internalTypes")}
                value={initialFilters['internal_ticket_type_ids']}
                onChange={(value) => onFilterChange('internal_ticket_type_ids', value)}
                className= 'w-100'
                enabledSearch={true}
              />
            </div>
          )}
        </>
      )} 
    </div>
  );
};

export default TicketFilter;
